import React from "react";
import { observer } from "mobx-react";
import { computed } from "mobx";
import { generatePath, withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import ContentNutritionRecipesStore from "stores/ContentNutritionRecipesStore";

import ConfirmationDialog from "components/ConfirmationDialog";
import Page from "components/Layout/Page";
import Placeholder from "components/Placeholder";
import RestoreArchivedButton from "components/RestoreArchivedButton";
import FilteringControl from "components/FilteringControl";
import SortingControl from "components/SortingControl";
import { SNACKBAR_TYPES } from "components/Snackbar";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import NavBar from "../NavBar";
import RecipeCard from "./RecipeCard";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import ROUTES from "navigation/routes";

import { TableSortType } from "stores/templates/TableStore";

import { getConfirmArchivationText } from "utils/publish";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  RecipeCardWrapper,
  ScrollLoader,
  OverflowLoader,
} from "./styles";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";
const MINIMUM_SEARCH_VALUE_LENGTH = 3;

@observer
class Recipes extends React.Component<Props> {
  @computed get recipes() {
    return ContentNutritionRecipesStore.items;
  }

  @computed get recipeListIsEmpty() {
    return this.recipes.length === 0;
  }

  componentDidMount() {
    ContentNutritionRecipesStore.fetch();
  }

  componentWillUnmount() {
    ContentNutritionRecipesStore.resetPage();
  }

  fetchMoreRecipes = async () => {
    const { setMessage } = this.props;

    try {
      await ContentNutritionRecipesStore.fetchMoreRecipes();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    if (value.length === 0) {
      this.onSearchClear();
      return;
    }

    ContentNutritionRecipesStore.applySearch(value);
    value.length >= MINIMUM_SEARCH_VALUE_LENGTH &&
      ContentNutritionRecipesStore.fetch();
  };

  onSearchClear = () => {
    ContentNutritionRecipesStore.clearSearch();
    ContentNutritionRecipesStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentNutritionRecipesStore.applyOrder(value);
    ContentNutritionRecipesStore.resetPage();
    ContentNutritionRecipesStore.fetch();
  };

  onFilterApply = (
    filterValues: { column: string; value: (string | number)[] }[],
  ) => {
    filterValues.map(filterValue =>
      ContentNutritionRecipesStore.applyFilter({
        column: filterValue.column,
        value: filterValue.value,
      }),
    );
    ContentNutritionRecipesStore.resetPage();
    ContentNutritionRecipesStore.fetch();
  };

  onFilterClear = () => {
    ContentNutritionRecipesStore.clearFilters();
    ContentNutritionRecipesStore.fetch();
  };

  onAddRecipe = () => {
    this.props.history.push({
      pathname: generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS),
      state: { editMode: true },
    });
  };

  onDetailsClick = (recipe: any) => () => {
    const path = generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
      recipeId: recipe.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: false } });
  };

  onEdit = (recipe: any) => () => {
    const path = generatePath(ROUTES.CONTENT_NUTRITION_RECIPE_DETAILS, {
      recipeId: recipe.id,
    });
    this.props.history.push({ pathname: path, state: { editMode: true } });
  };

  onArchive = (recipe: any) => () => {
    const { openPopup } = this.props;

    const recipeId = recipe.id;

    const text = getConfirmArchivationText({
      entity: recipe,
      suffix: "recipe",
    });

    openPopup({
      key: "ArchiveRecipe",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Recipe"}
          text={text}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={ContentNutritionRecipesStore.isRecipeManaging}
          onDecline={close}
          onAccept={this.archiveRecipe(recipeId, close)}
        />
      ),
    });
  };

  archiveRecipe = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await ContentNutritionRecipesStore.archiveRecipe(id);
      close();
      ContentNutritionRecipesStore.resetPage();
      ContentNutritionRecipesStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onPublish = (recipe: any) => async () => {
    const { setMessage } = this.props;
    const { id, publishStatus } = recipe;

    try {
      await ContentNutritionRecipesStore.updatePublishStatus(id, publishStatus);
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onCopy = (recipe: any) => async () => {
    const { setMessage } = this.props;
    const id = recipe.id;

    try {
      await ContentNutritionRecipesStore.copyRecipe(id);
      ContentNutritionRecipesStore.resetPage();
      ContentNutritionRecipesStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  render() {
    return (
      <Page
        title="Recipes"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <RestoreArchivedButton />
            </PageControl>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddRecipe}
              >
                Add Recipe
              </Button>
            </PageControl>
          </>
        }
        isLoading={ContentNutritionRecipesStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={ContentNutritionRecipesStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={
                  ContentNutritionRecipesStore.orderContext.appliedOrderInfo
                }
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
            <SearchControlWrapper>
              <FilteringControl
                initValue={ContentNutritionRecipesStore.filters}
                onApply={this.onFilterApply}
                onClear={this.onFilterClear}
                statusFiltering
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {(ContentNutritionRecipesStore.isRecipePublishing ||
          ContentNutritionRecipesStore.isRecipeCopying) && <OverflowLoader />}
        {!this.recipeListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.recipes.length}
            hasMore={ContentNutritionRecipesStore.hasMoreInfo}
            next={this.fetchMoreRecipes}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.recipes.map(recipe => (
              <RecipeCardWrapper key={recipe.id}>
                <RecipeCard
                  title={recipe.title}
                  duration={recipe.durationText}
                  image={recipe.imageUrl}
                  publishStatus={recipe.publishStatus}
                  onDetailsClick={this.onDetailsClick(recipe)}
                  onArchive={this.onArchive(recipe)}
                  onEdit={this.onEdit(recipe)}
                  onCopy={this.onCopy(recipe)}
                  onPublish={this.onPublish(recipe)}
                />
              </RecipeCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              ContentNutritionRecipesStore.search
                ? "No matches found. Please try another search."
                : "No recipes added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Recipes)));
